// Story 10.4 — Página de analytics. KPIs + charts. Visibilidad role-aware.

import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { AnalyticsToolbar } from '@/components/analytics/AnalyticsToolbar';
import { DailyTrendChart } from '@/components/analytics/DailyTrendChart';
import { EmptyAnalytics } from '@/components/analytics/EmptyAnalytics';
import { FunnelChart } from '@/components/analytics/FunnelChart';
import { HourlyHeatmap } from '@/components/analytics/HourlyHeatmap';
import { KpiGrid } from '@/components/analytics/KpiGrid';
import { TopErrorsTable } from '@/components/analytics/TopErrorsTable';
import { TopItemsList } from '@/components/analytics/TopItemsList';
import { useAnalytics } from '@/hooks/queries/use-analytics';
import { useBot } from '@/hooks/queries/use-bots';
import { detectPreset } from '@/lib/period-presets';

export function AnalyticsPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { botId?: string };
  const botId = Number(params.botId);
  const search = useSearch({ strict: false }) as { since?: string; until?: string };
  const navigate = useNavigate();

  const { data: bot } = useBot(botId, Number.isFinite(botId));
  // Story 16.2 — las métricas técnicas dependen del rol en la organización del
  // bot (el servidor ya no las manda sin `view_analytics_advanced`).
  const showTechnical = bot?.capabilities?.includes('view_analytics_advanced') ?? false;

  const queryParams = useMemo(
    () => ({ since: search.since, until: search.until }),
    [search.since, search.until],
  );
  const analytics = useAnalytics(botId, queryParams);

  const preset = useMemo(
    () => detectPreset(search.since, search.until),
    [search.since, search.until],
  );

  function updateRange(next: { since?: string; until?: string }): void {
    navigate({
      to: '/bots/$botId/analytics',
      params: { botId: String(botId) },
      search: () => ({ since: next.since, until: next.until }),
    });
  }

  if (analytics.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        No se pudo cargar el reporte de analytics.{' '}
        <button
          type="button"
          onClick={() => analytics.refetch()}
          className="underline underline-offset-2"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const report = analytics.data;
  // P-297: incluir `ordersCompleted` en el chequeo. Si el bot no tiene
  // mensajes pero sí completó pedidos (e.g. backfill manual de
  // `flow:completed`), NO debemos mostrar el EmptyState.
  const totalsAreEmpty =
    !!report &&
    report.totals.conversations === 0 &&
    report.totals.messagesReceived === 0 &&
    report.totals.messagesSent === 0 &&
    report.totals.ordersCompleted === 0;

  if (report && (report.partial || totalsAreEmpty)) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <EmptyAnalytics
          botId={botId}
          phoneNumberId={bot?.phoneNumberId ?? null}
          provider={
            ((
              (bot?.config as Record<string, unknown> | undefined)?.whatsapp as
                | Record<string, unknown>
                | undefined
            )?.provider as 'cloud-api' | 'evolution-go' | undefined) ?? 'cloud-api'
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground">
          Métricas de uso del bot. Se refresca cada 30 segundos.
        </p>
      </div>
      <AnalyticsToolbar
        preset={preset}
        customSince={preset === 'custom' ? search.since : undefined}
        customUntil={preset === 'custom' ? search.until : undefined}
        onChange={updateRange}
      />

      {analytics.isLoading || !report ? (
        <KpiGrid
          report={{
            period: { since: '', until: '' },
            totals: {
              conversations: 0,
              messagesReceived: 0,
              messagesSent: 0,
              ordersCompleted: 0,
            },
            hourlyDistribution: [],
            dailyDistribution: [],
            topItems: [],
            errorRate: 0,
            averageLatencyMs: 0,
            funnel: [],
            topErrors: [],
            partial: true,
          }}
          showTechnical={showTechnical}
          loading
        />
      ) : (
        <>
          <KpiGrid report={report} showTechnical={showTechnical} />
          <div className="grid gap-3 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <DailyTrendChart data={report.dailyDistribution} />
            </div>
            <HourlyHeatmap data={report.hourlyDistribution} />
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            <TopItemsList data={report.topItems} />
            {showTechnical && <TopErrorsTable errors={report.topErrors} />}
          </div>
          {showTechnical && <FunnelChart flows={report.funnel} />}
        </>
      )}
    </div>
  );
}
